/**
 * Single source of truth for the 7 use-case modules + Test Drive.
 *
 * Used by:
 *  - DashboardPage (compact card strip at the top of /dashboard)
 *  - TestDrivePage (sample-case picker)
 *  - WorkflowLayout (eyebrow / title / lede / steps for each module)
 *  - AppShell nav (optional, ordering)
 */
import { Icon } from './WorkflowIcons';

export const WORKFLOW_MODULES = [
  {
    id: 'denial-resolve',
    name: 'Denial Resolve',
    to: '/DenialResolve',
    icon: Icon.spark,
    short: 'Parse 835s, route every denial.',
    eyebrow: 'Denial Resolve',
    title: 'Upload an 835 ERA, route every denial',
    lede:
      'Drop in a remittance file. We parse every CARC/RARC, run the 12-component v2 engine, and route each line to Correction, Appeal, or Patient Responsibility -- with the reasoning shown.',
    uploadHint: 'Drag-drop an 835 ERA file, Excel pivot, PDF EOB, screenshots of denial letters, or a pre-built CM denial_payload .json case.',
    accepts: '.x12,.txt,.edi,.zip,.xlsx,.xls,.csv,.pdf,.json,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp',
    multiple: true,
    runLabel: 'Parse & route',
    standalone: true,
  },
  {
    id: 'claim-right',
    name: 'Claim Right',
    to: '/ClaimRight',
    icon: Icon.shield,
    short: 'Scrub 837s before submit.',
    eyebrow: 'Claim Right',
    title: 'Scrub a draft 837 before you submit',
    lede:
      'Catch unbundling, MUE caps, and missing 25 / 59 / X-modifiers before they ever come back denied. Same edit engines that drive Denial Resolve, run pre-submission.',
    uploadHint: 'Drop in a draft 837P / 837I -- JSON, X12, or a CSV of service lines. We will scrub it against NCCI / MUE / modifier rules.',
    accepts: '.json,.x12,.edi,.txt,.csv',
    multiple: false,
    runLabel: 'Scrub claim',
    standalone: true,
  },
  {
    id: 'medical-necessity',
    name: 'Medical Necessity',
    to: '/MedicalNecessity',
    icon: Icon.checkCircle,
    short: 'Check LCD/NCD support.',
    eyebrow: 'Medical Necessity',
    title: 'Confirm LCD / NCD support before you bill',
    lede:
      'Cross-check planned CPTs against the Medicare LCD / NCD / Article narratives and the DX-CPT coverage indexes -- the same evidence the medical-necessity specialist uses post-denial.',
    uploadHint: 'Enter CPT + ICD codes below, or drop in chart notes / op reports for the criteria walk-through.',
    accepts: '.pdf,.txt,.docx',
    multiple: true,
    runLabel: 'Check coverage',
    standalone: true,
  },
  {
    id: 'coding-intelligence',
    name: 'Coding Intelligence',
    to: '/CodingIntelligence',
    icon: Icon.code,
    short: 'Validate NCCI, MUE, modifiers.',
    eyebrow: 'Coding Intelligence',
    title: 'Validate NCCI / CCI, MUE and modifier usage',
    lede:
      'Deterministic NCCI PTP, MUE cap, and modifier-25/59/X edits against the Medicare lookup tables. Tells you exactly which codes to add, drop, or modify.',
    uploadHint: 'Drop a CSV / TXT of CPT codes (one per line) or type them into the form below.',
    accepts: '.csv,.txt',
    multiple: false,
    runLabel: 'Run coding check',
    standalone: true,
  },
  {
    id: 'auth-eligibility',
    name: 'Auth & Eligibility',
    to: '/AuthEligibility',
    icon: Icon.key,
    short: 'Reconcile coverage and PA.',
    eyebrow: 'Auth & Eligibility',
    title: 'Reconcile 270/271 coverage + prior auth',
    lede:
      'Run a live 270 / 271 against the configured payer adapter, then submit or look up the matching prior auth -- before a denial ever happens.',
    uploadHint: 'Enter member details below, or drop in a 270/271 file, an existing PA letter, or coverage screenshots.',
    accepts: '.x12,.edi,.txt,.pdf,.png,.jpg,.jpeg',
    multiple: true,
    runLabel: 'Check eligibility',
    standalone: true,
  },
  {
    id: 'chat',
    name: 'Chat',
    to: '/help/chat',
    icon: Icon.chat,
    short: 'Ask anything about denials, coding, or appeals.',
    eyebrow: 'Chat',
    title: 'AI Chat Assistant',
    lede: 'Ask questions about denials, coding, appeals, or any RCM topic and get instant expert guidance.',
    uploadHint: '',
    accepts: '',
    multiple: false,
    runLabel: '',
    standalone: true,
    skipUpload: true,
  },
];

export const TEST_DRIVE_CARD = {
  id: 'test-drive',
  name: 'Test Drive',
  to: '/test-drive',
  icon: Icon.rocket,
  short: 'Run sample cases to see the system work.',
  highlight: true,
};

/** Convenience: the dashboard card strip (Test Drive first, then the 7 modules). */
export const DASHBOARD_CARDS = [TEST_DRIVE_CARD, ...WORKFLOW_MODULES];

export function getWorkflowModule(id) {
  return WORKFLOW_MODULES.find((m) => m.id === id) || null;
}

export function getWorkflowModuleByPath(pathname) {
  const p = String(pathname || '').toLowerCase().replace(/\/+$/, '');
  return WORKFLOW_MODULES.find((m) => m.to.toLowerCase() === p) || null;
}

export default WORKFLOW_MODULES;
